import playerProfileImg from '../../images/player-profile.png';
import { PLAYER_BASE_URL } from '../../apiConfig';
import { Shield, Target, User, Globe, Hash, ExternalLink, Calendar, Home, Plane, CheckCircle } from 'lucide-react';

function MobPlayerProfileHeader({ data, teamName }) {
    const accentYellow = '#E7C424';

    const isOverseas = data.player_type?.toLowerCase().includes('overseas');
    const isHome = data.player_type?.toLowerCase().includes('home');
    const isVerified = data.documentVerification == 1;
    const iconColor = isVerified ? '#2ECC71' : accentYellow;

    let PlayerTypeIcon = <Shield size={12} color={iconColor} />;
    if (isHome) {
        PlayerTypeIcon = <Home size={12} color={iconColor} />;
    } else if (isOverseas) {
        PlayerTypeIcon = <Plane size={12} color={iconColor} />;
    }

    const styles = {
        wrapper: {
            width: '100%',
            background: 'linear-gradient(145deg, #0A1172 0%, #040960 100%)',
            padding: '32px 20px',
            boxSizing: 'border-box',
            color: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            alignItems: 'center',
            borderRadius: '12px',
            position: 'relative',
            overflow: 'hidden',
        },
        teamBadge: {
            background: 'rgba(231,196,36,0.15)',
            border: `1px solid ${accentYellow}`,
            borderRadius: '4px',
            padding: '4px 12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
        },
        teamText: {
            fontFamily: 'var(--fontDisplay)',
            fontWeight: 800,
            fontSize: '12px',
            color: accentYellow,
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        imageFrame: {
            width: '180px',
            height: '180px',
            border: `3px solid #FFFFFF`,
            borderRadius: '50%',
            overflow: 'hidden',
            background: '#FFFFFF',
            boxShadow: '0 10px 20px rgba(0,0,0,0.4)',
        },
        nameBox: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
        },
        firstName: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: '44px',
            lineHeight: 0.9,
            margin: 0,
            textTransform: 'uppercase',
            fontWeight: 900,
            letterSpacing: '-1px',
        },
        lastName: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: '18px',
            fontWeight: 800,
            margin: '4px 0 0 0',
            color: accentYellow,
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        infoGrid: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
            background: 'rgba(255,255,255,0.05)',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.1)',
        },
        infoBox: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 0',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
        },
        infoLabel: {
            fontFamily: 'var(--fontDisplay)',
            fontWeight: 800,
            fontSize: '11px',
            color: accentYellow,
            textTransform: 'uppercase',
        },
        infoValue: {
            fontFamily: 'var(--fontBody)',
            fontWeight: 700,
            fontSize: '14px',
        },
        statsRow: {
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            width: '100%',
            background: 'rgba(0,0,0,0.2)',
            padding: '20px',
            borderRadius: '12px',
        },
        statItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        statValue: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: '28px',
            fontWeight: 900,
            lineHeight: 1,
            color: accentYellow,
        },
        statLabel: {
            fontFamily: 'var(--fontDisplay)',
            fontSize: '11px',
            fontWeight: 800,
            opacity: 0.5,
            textTransform: 'uppercase',
        },
        playCricketBtn: {
            background: accentYellow,
            padding: '12px 24px',
            borderRadius: '4px',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none',
            color: '#0A1172',
            fontFamily: 'var(--fontDisplay)',
            fontWeight: 900,
            fontSize: '14px',
            textTransform: 'uppercase',
        }
    };

    return (
        <div style={styles.wrapper}>
            {/* Decoration */}
            <div style={{
                position: 'absolute', top: -50, right: -50,
                width: '150px', height: '150px', background: 'rgba(231,196,36,0.05)',
                filter: 'blur(50px)', borderRadius: '50%'
            }} />

            <div style={styles.teamBadge}>
                <Shield size={14} color={accentYellow} />
                <p style={styles.teamText}>{teamName || data.team_name || 'CLUB NAME'}</p>
            </div>
            
            <div style={styles.imageFrame}>
                <img 
                    src={data.Photo ? `${PLAYER_BASE_URL}/${data.Photo}` : playerProfileImg} 
                    alt={data.Forename} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
            </div>

            <div style={styles.nameBox}>
                <h1 style={styles.firstName}>{data.Forename || 'PLAYER'}</h1>
                <p style={styles.lastName}>{data.Surname || 'SURNAME'}</p>
            </div>

            <div style={styles.infoGrid}>
                {[
                    { label: 'Debut', value: data.Debut, icon: <Calendar size={12} /> },
                    { label: 'Batting', value: data.BatStyle, icon: <Target size={12} /> },
                    { label: 'Bowling', value: data.BowlStyle, icon: <Target size={12} /> },
                    { label: 'Age Group', value: data.AgeGroup, icon: <User size={12} /> },
                    { label: 'Type', value: data.player_type, icon: PlayerTypeIcon, customColor: iconColor },
                    { label: 'Verified', value: isVerified ? 'Yes' : 'No', icon: <CheckCircle size={12} />, customColor: iconColor },
                ].map((item, idx) => (
                    <div style={styles.infoBox} key={idx}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: item.customColor || accentYellow, display: 'flex', alignItems: 'center' }}>{item.icon}</span>
                            <span style={styles.infoLabel}>{item.label}</span>
                        </div>
                        <span style={styles.infoValue}>{item.value || 'N/A'}</span>
                    </div>
                ))}
            </div>

            <div style={styles.statsRow}>
                <div style={styles.statItem}>
                    <span style={styles.statValue}>{data.total_matchs || 0}</span>
                    <span style={styles.statLabel}>Matches</span>
                </div>
                <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
                <div style={styles.statItem}>
                    <span style={styles.statValue}>{data.total_runs || 0}</span>
                    <span style={styles.statLabel}>Runs</span>
                </div>
                <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
                <div style={styles.statItem}>
                    <span style={styles.statValue}>{data.total_wkts || 0}</span>
                    <span style={styles.statLabel}>Wickets</span>
                </div>
            </div>

            <a href={`https://play-cricket.com/player_stats/player/${data.pc_player_id}`} target="_blank" rel="noreferrer" style={styles.playCricketBtn}>
                <span>Play Cricket Profile</span>
                <ExternalLink size={16} />
            </a>

            <div style={{ display: 'flex', gap: '16px', marginTop: '4px', opacity: 0.4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Hash size={12} />
                    <span style={{ fontFamily: 'var(--fontBody)', fontSize: '12px', fontWeight: 600 }}>{data.PlayerID}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Globe size={12} />
                    <span style={{ fontFamily: 'var(--fontBody)', fontSize: '12px', fontWeight: 600 }}>{data.pc_player_id || 'N/A'}</span>
                </div>
            </div>
        </div>
    );
}

export default MobPlayerProfileHeader;